import axios from 'axios';
import {
    Button,
    Card,
    CardBody,
    CardPlaceholder,
    Icon,
    Icons,
    PageLayout,
    StringHelpers,
    useAuthorisationContext,
    useLangContext,
    checkTheme,
    Themes,
    Permissions,
    Variants,
    getRegistrationUrl,
    customToast,
    route,
    API_ENDPOINTS,
    replaceKeyWithValue,
    useDisabledContext,
    Roles,
} from 'carrier-fe';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function Dashboard() {
    const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();
    const navigate = useNavigate();
    const { disabled, setDisabled } = useDisabledContext();

    const { crud, responses, getLangPage } = useLangContext();
    const [lang, setLang] = useState<any>(null);

    useEffect(() => {
        if (lang === null) {
            load();
        }
    }, []);

    const load = async () => {
        getLangPage('admin/dashboard')
            .then((res: any) => {
                if (typeof res === 'undefined' || res === null) {
                    setLang([]);
                } else {
                    setLang(res);

                    document.title = StringHelpers.title(
                        res?.page.title ?? 'Dashboard'
                    );
                }
            })
            .catch((err: any) => console.error(err));
    };

    const syncSheets = async () => {
        customToast({
            title: crud?.buttons?.sync?.submitting || "Syncing...",
            variant: Variants.Info
        });

        setDisabled(true);
    
        try {
            await axios.post(route(API_ENDPOINTS.ADMIN.SYNC));

            customToast({
                title: replaceKeyWithValue(
                    responses?.sync,
                    "attribute",
                    StringHelpers.sentence(
                        responses?.models?.warranty_matrix
                    )
                ),
                variant: Variants.Success,
            });
        } catch (error: any) {
            customToast({
                title: error?.response?.data?.message,
                variant: Variants.Danger,
            });
        } finally {
            setDisabled(false);
        }
    }; 

    const getTileById = (id: string) => {
        if (!lang || !lang.page || !lang.page.tiles) return null;

        for (let i = 0; i < lang.page.tiles.length; i++) {
            if (lang.page.tiles[i]['id'] === id) {
                return lang.page.tiles[i];
            }
        }

        return null;
    };

    const renderCompaniesTile = () => {
        if (!hasPermissionAccess(Permissions.ADMIN_COMPANY_INDEX)) return null;

        let content = getTileById('admin.company.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/companies')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderUsersTile = () => {
        if (!hasPermissionAccess(Permissions.ADMIN_USER_INDEX)) return null;

        let content = getTileById('admin.user.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/user')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderTrainingCoursesTile = () => {
        if (!hasPermissionAccess(Permissions.ADMIN_TRAINING_COURSE_INDEX))
            return null;

        let content = getTileById('admin.training.course.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/training/course')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderTrainingEventsTile = () => {
        if (!hasPermissionAccess(Permissions.ADMIN_TRAINING_EVENT_INDEX))
            return null;

        let content = getTileById('admin.training.event.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/training/event')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderWarrantyTile = () => {
        const canAccessToshiba =
            checkTheme(Themes.Toshiba) &&
            hasPermissionAccess(Permissions.TOSHIBA_ADMIN_WARRANTY_INDEX) &&
            hasPermissionAccess(Permissions.TOSHIBA_WARRANTY_INDEX);

        const canAccessViessmann =
            checkTheme(Themes.Viessmann) &&
            hasPermissionAccess(Permissions.VIESSMANN_ADMIN_WARRANTY_INDEX) &&
            hasPermissionAccess(Permissions.VIESSMANN_WARRANTY_INDEX);

        if (!canAccessToshiba && !canAccessViessmann) return null;

        let content = getTileById('admin.warranty.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => 
                            window.location.href = getRegistrationUrl() + '/warranties'
                        }
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderSyncWarrantyTile = () => {
        if (!hasPermissionAccess(Permissions.SYSTEM_DATA_UPDATE)
            || checkTheme(Themes.Toshiba)
        ) {
            return null;
        }

        let content = getTileById('admin.warranty_sync.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Sync'
                        }
                        onClick={() => syncSheets()}
                        className="d-inline-flex"
                        variant={Variants.Info}
                        disabled={disabled}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderClaimsTile = () => {
        if (
            !checkTheme(Themes.Toshiba) ||
            !hasPermissionAccess(Permissions.TOSHIBA_ADMIN_CLAIM_INDEX)
        )
            return null;

        let content = getTileById('admin.claim.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/claims')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderDocumentsTile = () => {
        if (
            !checkTheme(Themes.Toshiba) ||
            !hasPermissionAccess(Permissions.DOCUMENT_INDEX)
        )
            return null;

        let content = getTileById('admin.document.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/documents')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderProductsTile = () => {
        if (
            !checkTheme(Themes.Toshiba) ||
            !hasPermissionAccess(Permissions.PRODUCT_INDEX)
        )
            return null;

        let content = getTileById('admin.product.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/products')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderMediaPacksTile = () => {
        if (
            !hasPermissionAccess(Permissions.ADMIN_MEDIA_PACK_INDEX)
            || (!checkTheme(Themes.Viessmann) && !checkTheme(Themes.Toshiba))
        )
            return null;

        let content = getTileById('admin.media_pack.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/media-packs')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    const renderCarouselSlidesTile = () => {
        if (!hasPermissionAccess(Permissions.ADMIN_CAROUSEL_SLIDE_INDEX))
            return null;

        let content = getTileById('admin.carousel_slide.index');

        return (
            <Card noMargin>
                <CardBody>
                    <Icon
                        icon={Icons.DASHBOARD}
                        style={{ color: '#ff3e17' }}
                    />
                    <h4 className="card-title">
                        {!!content && !!content.title ? content.title : ''}
                    </h4>

                    <Button
                        label={
                            !!content && !!content.button
                                ? content.button
                                : 'Go to'
                        }
                        onClick={() => navigate('/carousel-slides')}
                        className="d-inline-flex"
                        variant={Variants.Info}
                    />
                </CardBody>
            </Card>
        );
    };

    return (
        <PageLayout
            title={StringHelpers.title(lang?.page.title ?? 'Dashboard')}
        >
            <div className="dashboard--inner--wrapper d-grid gap-4">
                {lang === null ? (
                    <>
                        <CardPlaceholder />
                        <CardPlaceholder />
                        <CardPlaceholder />
                        <CardPlaceholder />
                    </>
                ) : (
                    <>
                        {renderCompaniesTile()}
                        {renderUsersTile()}
                        {renderTrainingCoursesTile()}
                        {renderTrainingEventsTile()}
                        {renderWarrantyTile()}
                        {renderSyncWarrantyTile()}
                        {renderClaimsTile()}
                        {renderDocumentsTile()}
                        {renderProductsTile()}
                        {renderMediaPacksTile()}
                        {renderCarouselSlidesTile()}
                    </>
                )}
            </div>
        </PageLayout>
    );
}

export default Dashboard;
